import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, lastValueFrom } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { ReferenzaModel } from '../core/models/api/referenza.model';
import { RuoloReferenzaAssignmentModel } from '../core/models/api/ruolo-referenza.model';

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  private readonly baseUrl = environment.backend.baseUrl;

  constructor(private http: HttpClient) {}

  // Esempio: recupero di alcuni dati protetti dal backend.
  // Il token Auth0 viene allegato automaticamente dall'interceptor configurato in AppModule.
  getProtectedData(): Observable<unknown> {
    return this.http.get(`${this.baseUrl}/protected/data`);
  }

  /**
   * Test di connettività verso il backend chiamando l'endpoint /health
   */
  async testHealth(): Promise<{
    connected: boolean;
    status?: number;
    error?: string;
    responseTime: number;
  }> {
    const startTime = Date.now();

    try {
      const response = await lastValueFrom(
        this.http.get(`${this.baseUrl}/health`, {
          observe: 'response',
        }),
      );

      const status = response.status;
      const connected = status >= 200 && status < 300;

      return {
        connected,
        status,
        responseTime: Date.now() - startTime,
      };
    } catch (error: unknown) {
      const anyError = error as { status?: number; message?: string };

      return {
        connected: false,
        status: anyError.status,
        error:
          anyError.message ??
          'Errore sconosciuto durante la chiamata di health',
        responseTime: Date.now() - startTime,
      };
    }
  }

  /**
   * Crea una nuova referenza chiamando l'API POST /referenza
   * Il DTO di creazione non include id, dataCreazione e dataModifica
   */
  createReferenza(
    referenza: Omit<ReferenzaModel, 'id' | 'dataCreazione' | 'dataModifica'>,
  ): Observable<ReferenzaModel> {
    // Normalizza i codici come stringhe prima di inviare
    const normalizedReferenza = {
      ...referenza,
      codiceCommerciale: referenza.codiceCommessa
        ? String(referenza.codiceCommessa)
        : null,
      codiceCertificato: referenza.codiceCertificato
        ? String(referenza.codiceCertificato)
        : null,
    };
    return this.http.post<ReferenzaModel>(
      `${this.baseUrl}/referenza`,
      normalizedReferenza,
    );
  }

  /**
   * Aggiorna una referenza esistente chiamando l'API PUT /referenza/{id}
   */
  updateReferenza(
    id: number,
    referenza: ReferenzaModel,
  ): Observable<ReferenzaModel> {
    // Normalizza i codici come stringhe prima di inviare
    const normalizedReferenza = {
      ...referenza,
      codiceCommerciale: referenza.codiceCommessa
        ? String(referenza.codiceCommessa)
        : null,
      codiceCertificato: referenza.codiceCertificato
        ? String(referenza.codiceCertificato)
        : null,
    };
    return this.http.put<ReferenzaModel>(
      `${this.baseUrl}/referenza/${id}`,
      normalizedReferenza,
    );
  }

  /**
   * Aggiorna solo la posizione di una referenza
   * PATCH /referenza/{id}/posizione
   */
  updateReferenzaPosizione(
    id: number,
    payload: { latitude: number | null; longitudine: number | null },
  ): Observable<void> {
    return this.http.patch<void>(
      `${this.baseUrl}/referenza/${id}/posizione`,
      payload,
    );
  }

  /**
   * Aggiorna i servizi accessori di una referenza
   * PATCH /referenza/{id}/servizi-accessori
   */
  updateServiziAccessori(
    id: number,
    payload:
      | { serviziAccessoriPresenti: true; urlServiziAccessori: string | null }
      | { serviziAccessoriPresenti: false },
  ): Observable<void> {
    return this.http
      .patch(`${this.baseUrl}/referenza/${id}/servizi-accessori`, payload, {
        responseType: 'text',
      })
      .pipe(map(() => undefined));
  }

  /**
   * Elimina una referenza chiamando l'API DELETE /referenza/{id}
   */
  deleteReferenza(id: number): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/referenza/${id}`);
  }

  /**
   * Recupera i ruoli (con profili assegnati) per una specifica referenza
   * GET /referenze/{idReferenza}/ruoli
   */
  getRuoliReferenza(idReferenza: number): Observable<RuoloReferenzaAssignmentModel[]> {
    return this.http.get<RuoloReferenzaAssignmentModel[]>(
      `${this.baseUrl}/referenze/${idReferenza}/ruoli`,
    );
  }

  /**
   * Assegna un profilo a un ruolo per una specifica referenza
   * POST /referenze/{idReferenza}/ruoli/{idRuolo}/profili/{idProfilo}
   */
  assignProfiloToRuolo(
    idReferenza: number,
    idRuolo: number,
    idProfilo: number,
    payload: { firmatario: boolean },
  ): Observable<void> {
    const url = `${this.baseUrl}/referenze/${idReferenza}/ruoli/${idRuolo}/profili/${idProfilo}`;
    return this.http.post<void>(url, payload);
  }

  /**
   * Assegna un profilo inserito manualmente a un ruolo per una specifica referenza
   * POST /referenze/{idReferenza}/ruoli/{idRuolo}/profili
   */
  assignManualProfiloToRuolo(
    idReferenza: number,
    idRuolo: number,
    payload: { nome: string; cognome: string; firmatario: boolean },
  ): Observable<void> {
    const url = `${this.baseUrl}/referenze/${idReferenza}/ruoli/${idRuolo}/profili`;
    return this.http.post<void>(url, payload);
  }

  /**
   * Rimuove l'assegnazione di un profilo a un ruolo per una specifica referenza
   * DELETE /referenze/{idReferenza}/ruoli/{idRuolo}/profili/{idProfilo}
   */
  removeProfiloFromRuolo(
    idReferenza: number,
    idRuolo: number,
    idProfilo: number,
  ): Observable<void> {
    const url = `${this.baseUrl}/referenze/${idReferenza}/ruoli/${idRuolo}/profili/${idProfilo}`;
    return this.http.delete<void>(url);
  }
}
